import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

// USBC Oil Pattern Conversion Formulas
const convertOilPatternAverage = (average: number, fromPattern: string, toPattern: string): number | null => {
  if (average <= 0 || fromPattern === toPattern) return null;
  
  const baseline = 110;
  const diff = average - baseline;
  
  const from = fromPattern.toLowerCase();
  const to = toPattern.toLowerCase();
  
  if (from === 'sport' && to === 'challenge') return Math.round(average + (diff * 0.091));
  if (from === 'sport' && (to === 'standard' || to === 'house')) return Math.round(average + (diff * 0.182));
  if (from === 'challenge' && to === 'sport') return Math.round(average - (diff * 0.091));
  if (from === 'challenge' && (to === 'standard' || to === 'house')) return Math.round(average + (diff * 0.1));
  if ((from === 'standard' || from === 'house') && to === 'sport') return Math.round(average - (diff * 0.182));
  if ((from === 'standard' || from === 'house') && to === 'challenge') return Math.round(average - (diff * 0.1));
  
  return null;
};

interface LeagueStat {
  leagueId: number;
  leagueName: string;
  average: number;
  handicap: number;
  gamesPlayed: number;
  lastCalculated: string;
}

interface BowlerStats {
  bowler: {
    lifetimeAverage: number;
    lifetimeHandicap: number;
  };
  globalAggregateStats: {
    weeklyAverage: number | string;
    weeklyHandicap: number | string;
    monthlyAverage: number | string;
    monthlyHandicap: number | string;
    yearlyAverage: number | string;
    yearlyHandicap: number | string;
  };
  allLeagueStats: LeagueStat[];
}

interface BowlerData {
  id: number;
  name: string;
  email: string;
  average: number;
  handicap: number;
  city?: string;
  country?: string;
  joinedAt: string;
}

interface ScoutSettings {
  hdcp_base_average: number;
  hdcp_percentage: number;
  hdcp_max: number;
}

const ScoutBowlerDetail: React.FC = () => {
  const { bowlerId } = useParams<{ bowlerId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [bowlerData, setBowlerData] = useState<BowlerData | null>(null);
  const [bowlerStats, setBowlerStats] = useState<BowlerStats | null>(null);
   const [showHelpModal, setShowHelpModal] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedGlobalTimeframe, setSelectedGlobalTimeframe] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');
  const [selectedAllLeaguesTimeframe, setSelectedAllLeaguesTimeframe] = useState<'weekly' | 'monthly' | 'yearly' | 'per_league'>('per_league');
  const [selectedAllLeagueId, setSelectedAllLeagueId] = useState<string>('all');
  
  const [oilPatternStats, setOilPatternStats] = useState<any>(null);
  const [oilPatternLoading, setOilPatternLoading] = useState(false);
  const [selectedOilPeriod, setSelectedOilPeriod] = useState<'all_time' | 'last_year' | 'last_month' | 'last_week'>('all_time');
  const [selectedOilPattern, setSelectedOilPattern] = useState<'all' | 'House' | 'Challenge' | 'Sport' | 'Not Set'>('all');
  const [oilBaseAverage, setOilBaseAverage] = useState<number>(200);
  const [oilHandicapPercentage, setOilHandicapPercentage] = useState<number>(80);
  
  const [scoutSettings, setScoutSettings] = useState<ScoutSettings>({
    hdcp_base_average: 200,
    hdcp_percentage: 80,
    hdcp_max: 40
  });
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const calculateCustomHandicap = (average: number): number => {
    const { hdcp_base_average, hdcp_percentage, hdcp_max } = scoutSettings;
    const hdcp = Math.floor((hdcp_base_average - average) * (hdcp_percentage / 100));
    return Math.min(Math.max(hdcp, 0), hdcp_max);
  };

  const fetchScoutSettings = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/scout/auth/profile`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userRole');
        navigate('/');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        if (data.scout) {
          setScoutSettings({
            hdcp_base_average: data.scout.hdcp_base_average,
            hdcp_percentage: parseFloat(data.scout.hdcp_percentage),
            hdcp_max: data.scout.hdcp_max
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch scout settings:', error);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/scout/auth/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(scoutSettings)
      });

      if (response.ok) {
        alert(t('scout.dashboard.settingsSaved'));
        setShowSettingsModal(false);
      } else {
        alert(t('scout.dashboard.settingsFailed'));
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert(t('scout.dashboard.settingsFailed'));
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchBowlerData = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/scout/bowlers/${bowlerId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userRole');
        navigate('/');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setBowlerData(data.bowler);
      }
    } catch (error) {
      console.error('Failed to fetch bowler data:', error);
    }
  };

  const fetchBowlerStats = async () => {
    try {
      setStatsLoading(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/scout/analytics/bowler/${bowlerId}/all-stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userRole');
        navigate('/');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setBowlerStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch bowler stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchOilPatternStats = async () => {
    setOilPatternLoading(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const params = new URLSearchParams({
        period: selectedOilPeriod,
        oilPattern: selectedOilPattern,
        baseAverage: oilBaseAverage.toString(),
        handicapPercentage: oilHandicapPercentage.toString()
      });

      const response = await fetch(`${apiUrl}/scout/bowlers/${bowlerId}/oil-pattern-stats?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userRole');
        navigate('/');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setOilPatternStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch oil pattern stats:', error);
    } finally {
      setOilPatternLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userRole = localStorage.getItem('userRole');
    
    if (!token || userRole !== 'scout') {
      navigate('/');
      return;
    }

    if (bowlerId) {
      fetchBowlerData();
      fetchBowlerStats();
      fetchScoutSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bowlerId]);

  useEffect(() => {
    if (bowlerId && bowlerData) {
      fetchOilPatternStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOilPeriod, selectedOilPattern, oilBaseAverage, oilHandicapPercentage]);

  const getDisplayedAllLeaguesStats = () => {
    if (!bowlerStats?.allLeagueStats) return [];

    if (selectedAllLeagueId === 'all') {
      return bowlerStats.allLeagueStats;
    }
    return bowlerStats.allLeagueStats.filter(stat => stat.leagueId.toString() === selectedAllLeagueId);
  };

  if (!bowlerData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 flex items-center justify-center">
        <div className="flex items-center space-x-3 text-white">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900">
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button onClick={() => navigate('/scout/dashboard')} className="text-white hover:text-emerald-300 transition duration-200">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-3xl font-bold text-white">{t('scout.bowlerDetail.title')}</h1>
            </div>
          </div>
        </div>
      </div>
      {/* Help Card */}
      
        <div
          className="w-16 h-16 bg-yellow-600/30 rounded-full flex items-center justify-center shadow-lg cursor-pointer fixed bottom-8 right-8 z-50"
          onClick={() => setShowHelpModal(true)}
        >
          <span className="text-5xl text-yellow-300 font-bold">?</span>
        </div>
      

      {/* Help Modal */}
      {showHelpModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={e => {
            if (e.target === e.currentTarget) setShowHelpModal(false);
          }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-lg w-full mx-4 p-8 relative"
           style={{
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#6366f1 #18181b'
            }}>
            {/* Cool ? Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-600/30 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-5xl text-blue-300 font-bold">?</span>
              </div>
            </div>
            {/* Instructions Body */}
            <h2 className="text-2xl font-bold text-white text-center mb-4">{t('scout.bowlerDetail.helpTitle')}</h2>
            <div className="text-blue-200 text-base whitespace-pre-line">
              {t('scout.bowlerDetail.helpContent')}
            </div>
            <button
              onClick={() => setShowHelpModal(false)}
              className="mt-8 w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-8">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-2xl">{bowlerData.name.charAt(0).toUpperCase()}</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{bowlerData.name}</h2>
                <p className="text-emerald-200">{bowlerData.email}</p>
                {(bowlerData.city || bowlerData.country) && (
                  <p className="text-emerald-300 text-sm mt-1">
                    📍 {[bowlerData.city, bowlerData.country].filter(Boolean).join(', ')}
                  </p>
                )}
                <div className="flex items-center space-x-4 mt-2 text-sm text-emerald-300">
                  <span>{t('scout.bowlerDetail.bowlerId')}: {bowlerData.id}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-emerald-200 text-sm">{t('scout.bowlerDetail.joined')}</p>
              
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-white mb-2">{t('scout.bowlerDetail.lifetimeStats')}</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white/5 rounded-lg p-2 border border-white/10">
                      <div className="text-center">
                        <p className="text-emerald-200 text-xs mb-1">{t('scout.bowlerDetail.avg')}</p>
                        <p className="text-lg font-bold text-white">{bowlerStats?.bowler?.lifetimeAverage ?? bowlerData.average}</p>
                      </div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2 border border-white/10">
                      <div className="text-center">
                        <p className="text-emerald-200 text-xs mb-1">{t('scout.bowlerDetail.hdcp')}</p>
                        <p className="text-lg font-bold text-white">{bowlerStats?.bowler?.lifetimeHandicap ?? bowlerData.handicap}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-white">{t('scout.bowlerDetail.customLifetime')}</h3>
                    <button onClick={() => setShowSettingsModal(true)} className="text-emerald-300 hover:text-emerald-200 transition flex-shrink-0" title="Edit custom handicap settings">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                  </div>
                  <div className="bg-emerald-600/10 rounded-lg p-2 border border-emerald-500/30">
                    <div className="text-xs text-emerald-300 mb-1.5 text-center truncate">
                      {t('scout.bowlerDetail.base')}: {scoutSettings.hdcp_base_average} | %: {scoutSettings.hdcp_percentage} | {t('scout.bowlerDetail.max')}: {scoutSettings.hdcp_max}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white/5 rounded-lg p-2 border border-white/10">
                        <div className="text-center">
                          <p className="text-emerald-200 text-xs mb-1">{t('scout.bowlerDetail.avg')}</p>
                          <p className="text-lg font-bold text-white">{bowlerStats?.bowler?.lifetimeAverage ?? bowlerData.average}</p>
                        </div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-2 border border-white/10">
                        <div className="text-center">
                          <p className="text-emerald-200 text-xs mb-1">{t('scout.bowlerDetail.hdcp')}</p>
                          <p className="text-lg font-bold text-white">{calculateCustomHandicap(bowlerStats?.bowler?.lifetimeAverage ?? bowlerData.average)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3">
                  <h3 className="text-lg font-semibold text-white whitespace-nowrap">{t('scout.bowlerDetail.allLeaguesCombined')}</h3>
                  <div className="flex items-center gap-2">
                    <select value={selectedGlobalTimeframe} onChange={(e) => setSelectedGlobalTimeframe(e.target.value as 'weekly' | 'monthly' | 'yearly')} className="bg-emerald-600/30 text-white text-sm rounded-lg px-3 py-2 border border-emerald-500/50 focus:outline-none focus:border-emerald-400 hover:bg-emerald-600/40 transition cursor-pointer appearance-none w-full sm:w-auto" style={{backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem'}}>
                      <option value="weekly" className="bg-emerald-900 text-white">{t('scout.bowlerDetail.lastWeek')}</option>
                      <option value="monthly" className="bg-emerald-900 text-white">{t('scout.bowlerDetail.lastMonth')}</option>
                      <option value="yearly" className="bg-emerald-900 text-white">{t('scout.bowlerDetail.lastYear')}</option>
                    </select>
                    <button onClick={() => setShowSettingsModal(true)} className="text-emerald-300 hover:text-emerald-200 transition flex-shrink-0" title="Edit custom handicap settings">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="text-center">
                      <p className="text-emerald-200 text-xs mb-1">{t('scout.bowlerDetail.average')}</p>
                      <p className="text-xl font-bold text-white">
                        {bowlerStats?.globalAggregateStats?.[selectedGlobalTimeframe === 'weekly' ? 'weeklyAverage' : selectedGlobalTimeframe === 'monthly' ? 'monthlyAverage' : 'yearlyAverage'] ?? 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="text-center">
                      <p className="text-emerald-200 text-xs mb-1">{t('scout.bowlerDetail.handicap')}</p>
                      <p className="text-xl font-bold text-white">
                        {(() => {
                          const avgKey = selectedGlobalTimeframe === 'weekly' ? 'weeklyAverage' : selectedGlobalTimeframe === 'monthly' ? 'monthlyAverage' : 'yearlyAverage';
                          const avg = bowlerStats?.globalAggregateStats?.[avgKey];
                          if (avg && typeof avg === 'number') {
                            return calculateCustomHandicap(avg);
                          }
                          return 'N/A';
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <h3 className="text-lg font-semibold text-white">{t('scout.bowlerDetail.allLeaguesBreakdown')}</h3>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select value={selectedAllLeagueId} onChange={(e) => setSelectedAllLeagueId(e.target.value)} className="bg-emerald-600/30 text-white text-xs rounded-lg px-2 py-1.5 border border-emerald-500/50 focus:outline-none focus:border-emerald-400 hover:bg-emerald-600/40 transition cursor-pointer appearance-none w-full sm:w-auto" style={{backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.25rem center', backgroundSize: '1em 1em', paddingRight: '1.5rem'}}>
                      <option value="all" className="bg-emerald-900 text-white">{t('scout.bowlerDetail.allLeagues')}</option>
                      {bowlerStats?.allLeagueStats?.map((stat: LeagueStat) => (
                        <option key={stat.leagueId} value={stat.leagueId} className="bg-emerald-900 text-white">{stat.leagueName}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {statsLoading ? (
                  <div className="bg-white/5 rounded-lg p-6 border border-white/10 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto mb-2"></div>
                    <p className="text-emerald-200 text-xs">{t('scout.bowlerDetail.loadingStats')}</p>
                  </div>
                ) : getDisplayedAllLeaguesStats().length === 0 ? (
                  <div className="bg-white/5 rounded-lg p-6 border border-white/10 text-center">
                    <p className="text-emerald-200 text-sm">{t('scout.bowlerDetail.noStatsAvailable')}</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2" style={{scrollbarWidth: 'thin', scrollbarColor: 'rgba(16, 185, 129, 0.5) rgba(255, 255, 255, 0.1)'}}>
                    {getDisplayedAllLeaguesStats().map((stat: any) => (
                      <div key={stat.leagueId} className={stat.leagueDescription === 'Side Action Only' ? 'bg-orange-500/10 rounded-lg p-3 border border-orange-400/40' : 'bg-white/5 rounded-lg p-3 border border-white/10'}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-white font-semibold text-sm">{stat.leagueName}</h4>
                          <span className={stat.leagueDescription === 'Side Action Only' ? 'text-orange-300 text-xs' : 'text-emerald-300 text-xs'}>{stat.leagueDescription === 'Side Action Only' ? `Side Action Only: ${stat.gamesPlayed}` : `${stat.gamesPlayed} ${stat.gamesPlayed === 1 ? 'game' : 'games'}`}</span>
                        </div>
                        <div className="flex gap-2 items-center">
                          <div className="bg-white/5 rounded-lg p-2 border border-white/10 min-w-[75px]">
                            <p className="text-emerald-200 text-xs mb-1 text-center">Avg</p>
                            <p className="text-lg font-bold text-white text-center">{stat.average > 0 ? stat.average : 'N/A'}</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2 border border-white/10 min-w-[75px]">
                            <p className="text-emerald-200 text-xs mb-1 text-center">Hdcp</p>
                            <p className="text-lg font-bold text-white text-center">{stat.handicap}</p>
                          </div>
                          <div className="flex-1 text-right">
                            <p className="text-emerald-400 text-xs">{new Date(stat.lastCalculated).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-3">{t('scout.bowlerDetail.oilPatternStats')}</h3>
              
              <div className="space-y-3 mb-4">
                <div className="flex gap-2 flex-wrap">
                  <select value={selectedOilPeriod} onChange={(e) => setSelectedOilPeriod(e.target.value as any)} className="bg-emerald-600/30 text-white text-sm rounded-lg px-3 py-2 border border-emerald-500/50 focus:outline-none focus:border-emerald-400 hover:bg-emerald-600/40 transition cursor-pointer appearance-none" style={{backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem'}}>
                    <option value="all_time" className="bg-emerald-900 text-white">{t('scout.bowlerDetail.allTime')}</option>
                    <option value="last_year" className="bg-emerald-900 text-white">{t('scout.bowlerDetail.lastYear')}</option>
                    <option value="last_month" className="bg-emerald-900 text-white">{t('scout.bowlerDetail.lastMonth')}</option>
                    <option value="last_week" className="bg-emerald-900 text-white">{t('scout.bowlerDetail.lastWeek')}</option>
                  </select>

                  <select value={selectedOilPattern} onChange={(e) => setSelectedOilPattern(e.target.value as any)} className="bg-emerald-600/30 text-white text-sm rounded-lg px-3 py-2 border border-emerald-500/50 focus:outline-none focus:border-emerald-400 hover:bg-emerald-600/40 transition cursor-pointer appearance-none" style={{backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem'}}>
                    <option value="all" className="bg-emerald-900 text-white">{t('scout.bowlerDetail.allPatterns')}</option>
                    <option value="House" className="bg-emerald-900 text-white">House</option>
                    <option value="Challenge" className="bg-emerald-900 text-white">Challenge</option>
                    <option value="Sport" className="bg-emerald-900 text-white">Sport</option>
                    <option value="Not Set" className="bg-emerald-900 text-white">{t('scout.bowlerDetail.notSet')}</option>
                  </select>
                </div>

                <div className="flex gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <label className="text-emerald-200 text-sm whitespace-nowrap">Base Avg:</label>
                    <input type="number" min="100" max="300" value={oilBaseAverage} onChange={(e) => setOilBaseAverage(parseInt(e.target.value) || 200)} className="bg-emerald-600/30 text-white text-sm rounded-lg px-3 py-2 border border-emerald-500/50 focus:outline-none focus:border-emerald-400 hover:bg-emerald-600/40 transition w-20" />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <label className="text-emerald-200 text-sm whitespace-nowrap">Hdcp %:</label>
                    <input type="number" min="0" max="100" value={oilHandicapPercentage} onChange={(e) => setOilHandicapPercentage(parseInt(e.target.value) || 80)} className="bg-emerald-600/30 text-white text-sm rounded-lg px-3 py-2 border border-emerald-500/50 focus:outline-none focus:border-emerald-400 hover:bg-emerald-600/40 transition w-20" />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mb-4 items-start">
                <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-[100px]">
                  <div className="text-center">
                    <p className="text-emerald-200 text-xs mb-1">{t('scout.bowlerDetail.cumulativeAvg')}</p>
                    <p className="text-xl font-bold text-white">{oilPatternLoading ? '...' : oilPatternStats?.cumulativeAverage || 'N/A'}</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-[100px]">
                  <div className="text-center">
                    <p className="text-emerald-200 text-xs mb-1">{t('scout.bowlerDetail.cumulativeHdcp')}</p>
                    <p className="text-xl font-bold text-white">{oilPatternLoading ? '...' : oilPatternStats?.cumulativeHandicap || 'N/A'}</p>
                  </div>
                </div>
                {!oilPatternLoading && oilPatternStats?.cumulativeAverage && selectedOilPattern !== 'all' && selectedOilPattern !== 'Not Set' && (
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <p className="text-emerald-200 text-xs mb-1">{t('scout.bowlerDetail.patternEquivalents')}</p>
                    <div className="text-xs text-emerald-300/80 space-y-1">
                      {selectedOilPattern.toLowerCase() === 'sport' && (
                        <>
                          <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'sport', 'challenge')} Challenge</div>
                          <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'sport', 'house')} House</div>
                        </>
                      )}
                      {selectedOilPattern.toLowerCase() === 'challenge' && (
                        <>
                          <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'challenge', 'sport')} Sport</div>
                          <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'challenge', 'house')} House</div>
                        </>
                      )}
                      {selectedOilPattern.toLowerCase() === 'house' && (
                        <>
                          <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'house', 'challenge')} Challenge</div>
                          <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'house', 'sport')} Sport</div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {oilPatternLoading ? (
                <div className="bg-white/5 rounded-lg p-6 border border-white/10 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto mb-2"></div>
                  <p className="text-emerald-200 text-xs">{t('scout.bowlerDetail.loadingStats')}</p>
                </div>
              ) : !oilPatternStats?.leagues || oilPatternStats.leagues.length === 0 ? (
                <div className="bg-white/5 rounded-lg p-6 border border-white/10 text-center">
                  <div className="text-3xl mb-2">🎳</div>
                  <p className="text-emerald-200 text-sm">{t('scout.bowlerDetail.noOilPatternData')}</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2" style={{scrollbarWidth: 'thin', scrollbarColor: 'rgba(16, 185, 129, 0.5) rgba(255, 255, 255, 0.1)'}}>
                  {oilPatternStats.leagues.map((league: any) => (
                    <div key={`${league.leagueId}-${league.oilPattern}`} className={league.leagueDescription === 'Side Action Only' ? 'bg-orange-500/10 rounded-lg p-4 border border-orange-400/40' : 'bg-white/5 rounded-lg p-4 border border-white/10'}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-white font-semibold">{league.leagueName}</h4>
                          <p className="text-emerald-300 text-xs">🎳 {league.oilPattern === 'Not Set' ? t('scout.bowlerDetail.notSet') : league.oilPattern}</p>
                        </div>
                        <span className={league.leagueDescription === 'Side Action Only' ? 'text-orange-300 text-sm' : 'text-emerald-300 text-sm'}>{league.leagueDescription === 'Side Action Only' ? `Side Action Only: ${league.gamesPlayed}` : `${league.gamesPlayed} ${league.gamesPlayed === 1 ? 'game' : 'games'}`}</span>
                      </div>
                      <div className="flex gap-3 items-start">
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-[90px]">
                          <p className="text-emerald-200 text-xs mb-1 text-center">{t('scout.bowlerDetail.average')}</p>
                          <p className="text-xl font-bold text-white text-center">{league.average > 0 ? league.average : 'N/A'}</p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-[90px]">
                          <p className="text-emerald-200 text-xs mb-1 text-center">{t('scout.bowlerDetail.handicap')}</p>
                          <p className="text-xl font-bold text-white text-center">{league.handicap}</p>
                        </div>
                        {league.average > 0 && league.oilPattern !== 'Not Set' && (
                          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <p className="text-emerald-200 text-[10px] mb-1">{t('scout.bowlerDetail.equivalents')}</p>
                            <div className="text-[10px] text-emerald-300/70 space-y-0.5">
                              {league.oilPattern.toLowerCase() === 'sport' && (
                                <>
                                  <div>≈ {convertOilPatternAverage(Number(league.average), 'sport', 'challenge')} Challenge</div>
                                  <div>≈ {convertOilPatternAverage(Number(league.average), 'sport', 'house')} House</div>
                                </>
                              )}
                              {league.oilPattern.toLowerCase() === 'challenge' && (
                                <>
                                  <div>≈ {convertOilPatternAverage(Number(league.average), 'challenge', 'sport')} Sport</div>
                                  <div>≈ {convertOilPatternAverage(Number(league.average), 'challenge', 'house')} House</div>
                                </>
                              )}
                              {(league.oilPattern.toLowerCase() === 'house' || league.oilPattern.toLowerCase() === 'standard') && (
                                <>
                                  <div>≈ {convertOilPatternAverage(Number(league.average), 'house', 'challenge')} Challenge</div>
                                  <div>≈ {convertOilPatternAverage(Number(league.average), 'house', 'sport')} Sport</div>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6 rounded-t-xl">
              <h2 className="text-xl font-bold">{t('scout.bowlerDetail.customHandicapSettings')}</h2>
              <p className="text-emerald-100 text-sm mt-1">{t('scout.bowlerDetail.useYourOwnParameters')}</p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('scout.dashboard.baseAverage')}</label>
                <input type="number" value={scoutSettings.hdcp_base_average} onChange={(e) => setScoutSettings({ ...scoutSettings, hdcp_base_average: parseInt(e.target.value) })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('scout.dashboard.percentage')}</label>
                <input type="number" step="0.1" value={scoutSettings.hdcp_percentage} onChange={(e) => setScoutSettings({ ...scoutSettings, hdcp_percentage: parseFloat(e.target.value) })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('scout.dashboard.maxHandicap')}</label>
                <input type="number" value={scoutSettings.hdcp_max} onChange={(e) => setScoutSettings({ ...scoutSettings, hdcp_max: parseInt(e.target.value) })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>

            <div className="flex space-x-3 p-6 bg-gray-50 rounded-b-xl">
              <button onClick={() => setShowSettingsModal(false)} className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition duration-200">{t('common.cancel')}</button>
              <button onClick={handleSaveSettings} disabled={savingSettings} className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold rounded-lg transition duration-200">
                {savingSettings ? t('common.Processing') : t('scout.dashboard.saveSettings')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScoutBowlerDetail;
